/**
 * Meta WhatsApp account health helpers (shared UI).
 */

export type MetaHealthStatus = 'available' | 'limited' | 'restricted' | 'blocked';

/** Dispatched after connect/sync so banner/menu refresh without full reload */
export const WHATSAPP_ACCOUNTS_UPDATED_EVENT = 'whatsapp-accounts-updated';

export function notifyWhatsAppAccountsUpdated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(WHATSAPP_ACCOUNTS_UPDATED_EVENT));
  }
}

export const META_BUSINESS_SUPPORT_URL =
  'https://business.facebook.com/business/loginpage/?next=https%3A%2F%2Fbusiness.facebook.com%2F%3Fnav_ref%3Dbiz_unified_f3_login_page_to_mbs&login_options%5B0%5D=FB&login_options%5B1%5D=IG&login_options%5B2%5D=SSO&config_ref=biz_login_tool_flavor_mbs';

export const CRITICAL_META_HEALTH: ReadonlySet<MetaHealthStatus> = new Set<MetaHealthStatus>([
  'restricted',
  'blocked',
]);

/** Statuses that show banner / menu alert (cannot send normally) */
export const ALERT_META_HEALTH: ReadonlySet<MetaHealthStatus> = new Set<MetaHealthStatus>([
  'restricted',
  'blocked',
]);

export function isCriticalMetaHealth(
  status: string | null | undefined
): status is 'restricted' | 'blocked' {
  return status === 'restricted' || status === 'blocked';
}

/** User-facing alerts: only restricted/blocked. Limited does not show banners. */
export function isAlertMetaHealth(
  status: string | null | undefined
): status is 'restricted' | 'blocked' {
  return isCriticalMetaHealth(status);
}

export function openMetaBusinessSupport(): void {
  window.open(META_BUSINESS_SUPPORT_URL, '_blank', 'noopener,noreferrer');
}

/** Shared user-facing copy: limitation is from Meta, not the product */
export function metaHealthAlertCopy(status: MetaHealthStatus | string | null | undefined): {
  title: string;
  body: string;
} {
  if (status === 'blocked') {
    return {
      title: 'WhatsApp bloqueada por Meta',
      body:
        'Meta bloqueó el envío en este número. Esta restricción la aplica Meta sobre tu WhatsApp Business; no es un bloqueo de la plataforma. Revisa el estado en Meta Business.',
    };
  }
  if (status === 'limited') {
    return {
      title: 'WhatsApp limitada por Meta',
      body:
        'Meta limitó el envío en este número. Esta limitación la aplica Meta sobre tu WhatsApp Business; no es un bloqueo de la plataforma. Revisa el estado en Meta Business.',
    };
  }
  return {
    title: 'WhatsApp restringida por Meta',
    body:
      'Meta restringió el envío en este número. Esta restricción la aplica Meta sobre tu WhatsApp Business; no es un bloqueo de la plataforma. Revisa el estado en Meta Business.',
  };
}

/** Badge label for WhatsApp page / admin when connection exists */
export function metaHealthBadgeLabel(
  metaHealth: string | null | undefined,
  localStatus?: string
): string {
  if (localStatus && localStatus !== 'active') {
    if (localStatus === 'pending_phone') return '⚠ Verificación pendiente';
    if (localStatus === 'pending_embed') return '⚠ Registro pendiente';
    return '○ Inactivo';
  }
  switch (metaHealth) {
    case 'restricted':
      return '⚠ Restringida por Meta';
    case 'blocked':
      return '● Bloqueada por Meta';
    case 'limited':
    case 'available':
    default:
      return '● Activo';
  }
}

export function metaHealthBadgeClass(
  metaHealth: string | null | undefined,
  localStatus?: string
): string {
  if (localStatus && localStatus !== 'active') {
    if (localStatus === 'pending_phone' || localStatus === 'pending_embed') {
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
    }
    return 'bg-gray-100 text-gray-800';
  }
  switch (metaHealth) {
    case 'restricted':
      return 'bg-orange-100 text-orange-800 hover:bg-orange-200';
    case 'blocked':
      return 'bg-red-100 text-red-800 hover:bg-red-200';
    case 'limited':
    case 'available':
    default:
      return 'bg-green-100 text-green-800 hover:bg-green-200';
  }
}

export function adminWhatsAppStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'available':
      return 'Activa';
    case 'limited':
      return 'Limitada';
    case 'restricted':
      return 'Restringida';
    case 'blocked':
      return 'Bloqueada';
    case 'none':
    default:
      return 'Sin conexión';
  }
}

export function adminWhatsAppStatusClass(status: string | null | undefined): string {
  switch (status) {
    case 'available':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'limited':
      return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
    case 'restricted':
      return 'bg-orange-100 text-orange-800 border border-orange-200';
    case 'blocked':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'none':
    default:
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
}
